// OnboardingTrackPage — For EXISTING signed-in users who haven't picked a track yet.
// Same UI as SignUp Step-1 but uses /api/tracks/select (no register) and
// auto-routes to /onboarding/video.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Cpu, Briefcase, Landmark, Bot, Rocket, Check, ArrowRight, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import { fetchTrackCatalogue, selectTrack } from '../lib/tracksApi';

const ICONS = {
  'graduation-cap': GraduationCap, 'cpu': Cpu, 'briefcase': Briefcase,
  'landmark': Landmark, 'bot': Bot, 'rocket': Rocket,
};
const COLOR_THEME = {
  blue:   { ring: 'ring-blue-400',   bg: 'from-blue-500 to-blue-700',     pill: 'bg-blue-50 text-blue-700' },
  indigo: { ring: 'ring-indigo-400', bg: 'from-indigo-500 to-indigo-700', pill: 'bg-indigo-50 text-indigo-700' },
  teal:   { ring: 'ring-teal-400',   bg: 'from-teal-500 to-teal-700',     pill: 'bg-teal-50 text-teal-700' },
  amber:  { ring: 'ring-amber-400',  bg: 'from-amber-500 to-amber-700',   pill: 'bg-amber-50 text-amber-700' },
  purple: { ring: 'ring-purple-400', bg: 'from-purple-500 to-purple-700', pill: 'bg-purple-50 text-purple-700' },
  rose:   { ring: 'ring-rose-400',   bg: 'from-rose-500 to-rose-700',     pill: 'bg-rose-50 text-rose-700' },
};

export default function OnboardingTrackPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate('/signup', { replace: true }); return; }
    if (user.is_admin) { navigate('/dashboard', { replace: true }); return; }
    fetchTrackCatalogue()
      .then((c) => setTracks(c.tracks || []))
      .catch(() => setError('Could not load tracks.'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleSelect = async () => {
    if (!chosen) { setError('Pick a track first.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await selectTrack(chosen.id);
      await refresh();
      navigate('/onboarding/video', { replace: true });
    } catch {
      setError('Could not save track. Try again.');
      setSubmitting(false);
    }
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar/>
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Pick your track</h1>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              You're already signed in. Pick the track that fits YOU best — we'll guide the rest.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tracks.map((t) => {
              const Icon = ICONS[t.icon] || GraduationCap;
              const theme = COLOR_THEME[t.color] || COLOR_THEME.teal;
              const selected = chosen?.id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setChosen(t)}
                  className={`text-left bg-white border rounded-2xl p-5 hover:shadow-lg transition-all ${selected ? `ring-2 ${theme.ring} border-transparent shadow-lg` : 'border-gray-200'}`}
                  data-testid={`onboarding-track-${t.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.bg} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white"/>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{t.tagline}</p>
                  <div className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded ${theme.pill} mb-3`}>
                    {t.audience}
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-xl font-bold text-gray-900">₹{t.price_inr}</span>
                    {t.original_inr > t.price_inr && (
                      <>
                        <span className="text-sm text-gray-400 line-through">₹{t.original_inr}</span>
                        <span className="text-xs font-bold text-emerald-600">{t.discount_pct}% off</span>
                      </>
                    )}
                    <span className="text-xs text-gray-500">/ year</span>
                  </div>
                  {selected && (
                    <div className="flex items-center gap-1 text-xs font-semibold text-teal-700">
                      <Check size={14}/> Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {error && <div className="mt-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 max-w-md mx-auto text-center">{error}</div>}

          <div className="flex justify-center mt-8">
            <Button size="lg" className="btn-primary" disabled={!chosen || submitting} onClick={handleSelect} data-testid="onboarding-track-continue-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
              Continue <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
